/**
 * Transaction data export — the wire shapes and the client's small rules.
 *
 * The Export system action (`{"handler": "system", "system_op": "export"}`)
 * and its templates: standard ones in the model (`export.templates`),
 * organization ones in the tenant DB. The engine
 * (`twasta_runtime_engine.export`) does every read and write; a host supplies
 * an {@link ExportTransport} on `TxnRuntime.exportApi` and the shared screen
 * mounts the Export window and the template manager itself.
 * Doc: docs/plans/transaction-data-export.md.
 */
import config from './exportConfig.json'

export const EXPORT_CONFIG = config
export const AS_ON_SCREEN_ID = config.asOnScreenId

export type ExportFormat = 'xlsx' | 'csv' | 'json'
export type ValueMode = 'display' | 'code' | 'both'
export type LayoutMode = 'separate_sheets' | 'same_sheet'
export type HeaderOnLines = 'repeat_all' | 'repeat_keys' | 'first_only'
export type ExportPlace = 'list' | 'bulk' | 'record'

export interface ExportCatalogColumn {
  key: string
  label: string
  list_label: string
  kind: string
  /** A picklist / boolean / person: it has a stored code AND a label. */
  coded: boolean
  person: boolean
  pk: boolean
  /** Masked unless the reader holds the column's reveal role. */
  secure: boolean
  /** The organization's own field (additional attribute). */
  uda: boolean
  hidden: boolean
}

export interface ExportCatalogForm {
  form_no: string
  label: string
  header: boolean
  columns: ExportCatalogColumn[]
}

export interface ExportTemplateColumn {
  column: string
  heading: string
  value: ValueMode
  /** Header columns only: identifies a record (repeated on line rows). */
  key?: boolean
}

export interface ExportTemplateDetail {
  form_no: string
  sheet: string
  columns: ExportTemplateColumn[]
}

export interface ExportTemplate {
  id: string
  name: string
  description: string
  is_default: boolean
  /** People may leave out some of the template's forms when exporting. */
  form_choice: boolean
  roles: string[]
  format: ExportFormat
  file_name: string
  header: ExportTemplateColumn[]
  details: ExportTemplateDetail[]
  layout: {
    mode: LayoutMode
    header_on_lines: HeaderOnLines
    records_without_lines: 'include' | 'omit'
    group_heading_row: boolean
    header_sheet: string
  }
  sheet: { freeze_header: boolean; filter_buttons: boolean; about_sheet: boolean }
  // Server-decorated (read-only):
  scope?: 'standard' | 'org' | 'screen'
  version?: number
  missing?: string[]
  forms?: string[]
  updated_by?: string
  updated_at?: string
}

export interface ExportInfo {
  obj_name: string
  screen: string
  action: {
    name: string
    label: string
    formats: ExportFormat[]
    places: ExportPlace[]
    offer_list_view: boolean
    org_templates: boolean
    require_reason: boolean
  }
  max_rows: number
  sync_max_rows: number
  catalog: ExportCatalogForm[]
  templates: ExportTemplate[]
  default_template: string
  can_manage: boolean
}

export interface ManageTemplates {
  obj_name: string
  screen: string
  org_templates: boolean
  formats: ExportFormat[]
  catalog: ExportCatalogForm[]
  standard: ExportTemplate[]
  org: ExportTemplate[]
}

export interface ExportPreviewSheet {
  name: string
  groups: Array<[string, number]>
  headings: string[]
  parts: Array<'h' | 'd'>
  numeric: boolean[]
  rows: string[][]
}

export interface ExportPreview {
  records: number
  missing: string[]
  sheets: ExportPreviewSheet[]
  json: unknown[] | null
}

/** Which records: the list the person is looking at (re-run on the server
 *  with every read rule), or explicit record ids. */
export type ExportRows =
  | { mode: 'query'; query: Record<string, unknown> }
  | { mode: 'ids'; ids: string[] }

export interface ExportRequest {
  obj: string
  template_id: string
  /** "As on screen" only: the list's visible column keys. */
  columns?: string[]
  forms?: string[]
  format?: ExportFormat
  rows: ExportRows
  file_name?: string
  reason?: string
  delimiter?: string
}

export interface ExportJob {
  job_id: string
  status: 'running' | 'done' | 'failed' | 'cancelled'
  error?: string | null
  code?: string | null
  filename?: string | null
  mime?: string | null
  size?: number | null
  rows?: number | null
  records?: number | null
  total?: number | null
  skipped?: number | null
  truncated?: boolean | null
  missing?: string[] | null
  notices?: string[] | null
  background?: boolean
  estimate?: number | null
}

/** A host's export endpoints. Platform: `/api/txn-export/{project}/…`;
 *  downloaded runtime: `/app/{project}/api/txn-export/…`. */
export interface ExportTransport {
  info(obj: string): Promise<ExportInfo>
  preview(body: ExportRequest | (Partial<ExportRequest> & { obj: string; draft: ExportTemplate })): Promise<ExportPreview>
  start(body: ExportRequest): Promise<ExportJob>
  job(jobId: string): Promise<ExportJob>
  cancel(jobId: string): Promise<void>
  /** The finished file, fetched with the caller's credentials. */
  download(jobId: string): Promise<Blob>
  manageTemplates(obj: string): Promise<ManageTemplates>
  saveTemplate(obj: string, template: ExportTemplate, expectVersion?: number | null): Promise<ExportTemplate>
  deleteTemplate(obj: string, templateId: string): Promise<void>
}

// ── the model's Export action, read by the screen ─────────────────────────

export interface ExportActionConfig {
  name: string
  label: string
  places: ExportPlace[]
  /** The list's Export: a button on the bar, or (default) in the ⋮ menu. */
  placement: 'bar' | 'menu'
}

type AnyAction = { handler?: unknown; system_op?: unknown; disabled?: unknown; name?: unknown;
  label?: unknown; placement?: unknown; export?: { places?: unknown } }

/** The ENABLED Export system action of a transaction (transaction-level or
 *  header-form actions), or null. Mirrors `export.model.export_action_of`. */
export function exportActionOf(model: unknown): ExportActionConfig | null {
  const m = (model || {}) as { actions?: unknown; transaction?: { actions?: unknown };
    sql_models?: unknown[] }
  const txn = (m.transaction && typeof m.transaction === 'object') ? m.transaction : m
  const lists: unknown[] = []
  if (Array.isArray(txn.actions)) lists.push(...txn.actions)
  const sms = Array.isArray((txn as { sql_models?: unknown[] }).sql_models)
    ? (txn as { sql_models: unknown[] }).sql_models : []
  const first = sms[0] as { sql_model?: { actions?: unknown[] }; actions?: unknown[] } | undefined
  const headerActions = first?.sql_model?.actions || first?.actions
  if (Array.isArray(headerActions)) lists.push(...headerActions)
  for (const raw of lists) {
    const a = raw as AnyAction
    if (!a || typeof a !== 'object') continue
    if (String(a.handler || '').toLowerCase() !== 'system') continue
    if (String(a.system_op || '').toLowerCase() !== 'export') continue
    if (a.disabled === true || a.disabled === 'true') return null
    const places = Array.isArray(a.export?.places)
      ? (a.export!.places as string[]).filter(p => config.places.some(x => x.id === p))
      : [...config.defaultPlaces]
    const name = String(a.name || 'Export').trim() || 'Export'
    return { name, label: String(a.label || name).trim() || name, places: places as ExportPlace[],
      placement: a.placement === 'bar' ? 'bar' : 'menu' }
  }
  return null
}

// ── small helpers the editor and the window share ─────────────────────────

/** Move one item of a list from `from` to the insert position `to`
 *  (`to` counts positions in the ORIGINAL list, so "after the last row" is
 *  `list.length`). */
export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from < 0 || from >= list.length) return list
  const out = list.slice()
  const [item] = out.splice(from, 1)
  const at = from < to ? to - 1 : to
  out.splice(Math.max(0, Math.min(out.length, at)), 0, item)
  return out
}

/** Insert `item` at position `to` (clamped). */
export function insertAt<T>(list: T[], item: T, to: number): T[] {
  const out = list.slice()
  out.splice(Math.max(0, Math.min(out.length, to)), 0, item)
  return out
}

/** The form numbers a template exports (the header + its line forms). */
export function templateForms(t: ExportTemplate, catalog: ExportCatalogForm[]): string[] {
  if (!catalog.length) return []
  const have = new Set(catalog.slice(1).map(f => f.form_no))
  return [catalog[0].form_no, ...t.details.filter(d => have.has(d.form_no) && d.columns.length).map(d => d.form_no)]
}

export function blankTemplate(catalog: ExportCatalogForm[], screen: string): ExportTemplate {
  const header = catalog[0]
  const pk = (header?.columns || []).filter(c => c.pk)
  return {
    id: `org-${Date.now().toString(36)}`,
    name: 'New template',
    description: '',
    is_default: false,
    form_choice: true,
    roles: [],
    format: 'xlsx',
    file_name: `${screen || 'Export'} {date}`,
    header: pk.map(c => ({ column: c.key, heading: c.label, value: 'display' as ValueMode, key: true })),
    details: [],
    layout: { mode: 'separate_sheets', header_on_lines: 'repeat_keys', records_without_lines: 'include',
      group_heading_row: true, header_sheet: '' },
    sheet: { freeze_header: true, filter_buttons: true, about_sheet: true },
  }
}

/** A deep copy that keeps only the stored template fields (no decorations). */
export function cleanTemplate(t: ExportTemplate): ExportTemplate {
  const { scope: _s, version: _v, missing: _m, forms: _f, updated_by: _u, updated_at: _a, ...rest } = t
  return JSON.parse(JSON.stringify(rest)) as ExportTemplate
}

export function formatLabel(id: string): string {
  return config.formats.find(f => f.id === id)?.label || id.toUpperCase()
}

export function layoutSummary(t: ExportTemplate): string {
  const lines = t.details.filter(d => d.columns.length)
  if (!lines.length) return 'Records only'
  if (t.layout.mode === 'separate_sheets') return `Separate sheets (${1 + lines.length})`
  const how = { repeat_all: 'header repeated', repeat_keys: 'keys repeated', first_only: 'header once' }[t.layout.header_on_lines]
  return `Lines on the same sheet · ${how}`
}

/** A refusal from the export endpoints, as words to show. */
export function exportErrorText(err: unknown): { message: string; problems: string[] } {
  const e = err as { response?: { status?: number; data?: { detail?: unknown; error?: unknown } };
    status?: number; detail?: unknown; message?: string }
  // The platform flattens a refusal to the body itself ({error, code,
  // problems}); FastAPI's default and the runtime keep it under `detail`.
  const body = e?.response?.data
  const flat = body && typeof body === 'object' && (body as { error?: unknown }).error !== undefined
    && (body as { detail?: unknown }).detail === undefined ? body : undefined
  const detail = (flat ?? body?.detail ?? e?.detail) as
    { error?: string; message?: string; problems?: string[]; code?: string } | string | undefined
  const status = e?.response?.status ?? e?.status
  // A server that has no export endpoints at all (an older J2EE front)
  // answers a bare 404 — say so instead of "Not Found".
  if (status === 404 && !(detail && typeof detail === 'object' && detail.code)) {
    if (typeof detail !== 'string' || /^not found$/i.test(detail)) {
      return { message: 'Export is not available on this server.', problems: [] }
    }
  }
  if (detail && typeof detail === 'object') {
    return { message: detail.error || detail.message || 'The export failed.', problems: detail.problems || [] }
  }
  if (typeof detail === 'string' && detail) return { message: detail, problems: [] }
  return { message: e?.message || 'The export failed.', problems: [] }
}

/** Save a Blob as a file in the person's browser. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}
